<template>
  <div>
    <main-loading v-if="isSaving"></main-loading>
    <div v-if="isLoading" class="container">
      <div class="row">
        <div class="col-12 text-center">
          <fp-loading></fp-loading>
        </div>
      </div>
    </div>
    <div v-else class="container faq-container" ref="container">
      <nav v-if="apartmentId" aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="`/main/${apartmentId}`">หน้าหลัก</router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            คำถามที่พบบ่อย
          </li>
        </ol>
      </nav>
      <div class="row">
        <div class="col-12 col-lg-10 offset-lg-1">
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <h3 class="mb-0">จัดการคำถามที่พบบ่อย</h3>
            <small class="text-muted">เพิ่มได้สูงสุด {{ maxFaq }} คำถาม</small>
          </div>
          <p class="text-muted mt-2">
            ข้อมูลคำถาม-คำตอบเหล่านี้จะแสดงบนหน้าประกาศของคุณ เพื่อช่วยตอบข้อสงสัยของผู้สนใจ
          </p>
          <div class="alert alert-info mt-3" role="alert" v-if="faqs.length < maxFaq">
            แนะนำให้กรอกคำถาม-คำตอบให้ครบถ้วนและกระชับ เพื่อให้ผู้สนใจตัดสินใจได้รวดเร็วขึ้น
          </div>
          <div v-else class="alert alert-warning mt-3" role="alert">
            คุณเพิ่มคำถามครบ {{ maxFaq }} ข้อแล้ว หากต้องการเพิ่มคำถามใหม่ กรุณาลบคำถามที่ไม่จำเป็นออกก่อน
          </div>

          <div v-if="!faqs.length" class="card mt-4 shadow-sm">
            <div class="card-body text-center text-muted">
              ยังไม่มีคำถามในระบบ คลิกปุ่ม "เพิ่มคำถาม" เพื่อเริ่มต้นสร้าง FAQ แรกของคุณ
            </div>
          </div>

          <div v-else class="table-responsive mt-4">
            <table class="table table-bordered table-striped faq-table mb-0">
              <thead>
                <tr>
                  <th class="text-center" style="width: 80px;">ลำดับ</th>
                  <th style="width: 35%;">คำถาม *</th>
                  <th>คำตอบ *</th>
                  <th class="text-center" style="width: 140px;">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(faq, index) in faqs" :key="faq.localId" ref="faqRows">
                  <td class="align-middle text-center">{{ index + 1 }}</td>
                  <td>
                    <div class="form-group mb-0">
                      <input
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasQuestionError(index) }"
                        placeholder="กรุณาระบุคำถาม เช่น มีที่จอดรถหรือไม่?"
                        v-model="faq.question"
                        :disabled="isSaving"
                      />
                      <div class="invalid-feedback d-block" v-if="hasQuestionError(index)">
                        กรุณากรอกคำถาม
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="form-group mb-0">
                      <textarea
                        rows="3"
                        class="form-control"
                        :class="{ 'is-invalid': hasAnswerError(index) }"
                        placeholder="กรุณาระบุคำตอบ เช่น มีที่จอดรถ 1 คันต่อห้อง และมีที่จอดเพิ่มเติมคิดค่าบริการเดือนละ ..."
                        v-model="faq.answer"
                        :disabled="isSaving"
                      ></textarea>
                      <div class="invalid-feedback d-block" v-if="hasAnswerError(index)">
                        กรุณากรอกคำตอบ
                      </div>
                    </div>
                  </td>
                  <td class="align-middle text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="isSaving"
                      @click="removeFaq(index)"
                    >
                      <i class="fa fa-trash"></i>
                      ลบ
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4">
            <button type="button" class="btn btn-outline-primary" @click="addFaq" :disabled="!canAddMore || isSaving">
              <i class="fa fa-plus"></i>
              เพิ่มคำถาม
            </button>
            <small class="text-muted">
              เหลืออีก {{ remainingSlots }} คำถามที่สามารถเพิ่มได้
            </small>
          </div>

          <div class="card mt-4 shadow-sm" v-if="availableSuggestions.length">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">คำถามแนะนำ</h5>
                <small class="text-muted">เลือกได้อีก {{ availableSuggestions.length }} ข้อ</small>
              </div>
              <p class="text-muted mt-2 mb-3">
                คลิกเพื่อเพิ่มคำถามสามารถปรับแก้เนื้อหาได้ภายหลัง
              </p>
              <div class="list-group">
                <div
                  class="list-group-item d-flex justify-content-between align-items-start"
                  v-for="suggestion in availableSuggestions"
                  :key="suggestion.objectId || suggestion.question"
                  :class="{ disabled: isSuggestionBusy(suggestion) }"
                >
                  <div>
                    <div class="font-weight-bold text-left">{{ suggestion.question }}</div>
                  </div>
                  <div class="d-flex align-items-center">
                    <button type="button" class="btn btn-sm btn-outline-primary mr-2"
                      @click="onSuggestionAdd(suggestion)"
                      :disabled="isSuggestionBusy(suggestion)">
                      <i class="fa fa-plus"></i> เพิ่มคำถามนี้
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger"
                      @click="deleteSuggestion(suggestion)"
                      :disabled="isSuggestionBusy(suggestion)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button type="button" class="btn btn-primary btn-block mt-4 shadow" @click="saveFaqs"
            :disabled="isSaving || !faqs.length">
            <i class="fas fa-save" v-if="!isSaving"></i>
            <i class="fas fa-sync-alt fa-spin" v-else></i>
            บันทึกคำถาม
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      maxFaq: 50,
      isLoading: true,
      isSaving: false,
      apartmentId: null,
      sessionToken: null,
      draftApartmentInfo: null,
      faqs: [],
      suggestedFaqs: [],
      faqObjects: {},
      validationErrors: {},
      deletingSuggestionKey: null,
    };
  },
  computed: {
    canAddMore() {
      return this.faqs.length < this.maxFaq;
    },
    remainingSlots() {
      const remaining = this.maxFaq - this.faqs.length;
      return remaining >= 0 ? remaining : 0;
    },
    availableSuggestions() {
      return this.suggestedFaqs.filter((suggestion) => !suggestion.used);
    },
  },
  async mounted() {
    try {
      const user = this.Parse.User.current();
      const apartmentId = this.$route.params.apartmentId;
      if (!user || !apartmentId) {
        this.$router.push({ name: "Error", params: { message: "error" } });
        return;
      }
      this.apartmentId = apartmentId;
      this.sessionToken = user.getSessionToken();
      await this.loadDraftApartment();
    } catch (error) {
      this.$router.push({ name: "Error", params: { message: "error" } });
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async loadDraftApartment() {
      const Apartment = this.Parse.Object.extend("Apartment");
      const apartment = new Apartment();
      apartment.id = this.apartmentId;
      const draftQuery = new this.Parse.Query("DraftApartmentInfo");
      draftQuery.equalTo("apartment", apartment);
      this.draftApartmentInfo = await draftQuery.first();
      if (!this.draftApartmentInfo) {
        this.$bvToast.toast("กรุณากรอกข้อมูลประกาศให้เรียบร้อยก่อนจัดการ FAQ", {
          title: "ยังไม่พบข้อมูลประกาศ",
          variant: "warning",
          solid: true,
        });
        this.$router.push({
          name: "ApartmentRegister",
          params: { apartmentId: this.apartmentId },
        });
        return;
      }
      await this.loadFaqEntries();
    },
    async loadFaqEntries() {
      if (!this.draftApartmentInfo) {
        return;
      }
      const faqQuery = new this.Parse.Query("DraftFAQ");
      faqQuery.equalTo("apartmentInfo", this.draftApartmentInfo);
      faqQuery.limit(1000);
      faqQuery.ascending("createdAt");
      const faqResults = await faqQuery.find();

      const activeFaqs = [];
      const suggestedFaqs = [];
      const faqMap = {};

      faqResults.forEach((faqObject) => {
        const baseData = {
          question: faqObject.get("question") || "",
          answer: faqObject.get("answer") || "",
          objectId: faqObject.id,
        };
        faqMap[faqObject.id] = faqObject;
        if (faqObject.get("isActive")) {
          activeFaqs.push(this.createFaq(baseData, faqObject.id));
        } else {
          suggestedFaqs.push({ ...baseData, used: false });
        }
      });

      this.faqObjects = faqMap;
      this.faqs = activeFaqs.slice(0, this.maxFaq);
      this.suggestedFaqs = suggestedFaqs;
      this.suggestedFaqs.sort((a, b) => (a.question || "").localeCompare(b.question || ""));
      this.validationErrors = {};
    },
    createFaq(faq = {}, id) {
      const objectId = faq.objectId || id || null;
      return {
        localId: objectId || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        objectId,
        question: faq.question || "",
        answer: faq.answer || "",
      };
    },
    addFaq() {
      if (!this.canAddMore) return;
      this.faqs.push(this.createFaq());
    },
    removeFaq(index) {
      if (index < 0 || index >= this.faqs.length) return;
      const removedItems = this.faqs.splice(index, 1);
      const removedFaq = removedItems.length ? removedItems[0] : null;
      if (removedFaq && removedFaq.objectId) {
        const suggestionIndex = this.suggestedFaqs.findIndex(
          (suggestion) => suggestion.objectId === removedFaq.objectId
        );
        if (suggestionIndex !== -1) {
          this.$set(this.suggestedFaqs, suggestionIndex, {
            ...this.suggestedFaqs[suggestionIndex],
            used: false,
          });
        }
      }
      this.validationErrors = {};
    },
    isSuggestionBusy(_suggestion) {
      return this.isSaving || !!this.deletingSuggestionKey;
    },
    getSuggestionKey(suggestion = {}) {
      return suggestion.objectId || suggestion.question || null;
    },
    onSuggestionAdd(suggestion) {
      if (!suggestion || this.isSaving) {
        return;
      }
      const key = this.getSuggestionKey(suggestion);
      if (this.deletingSuggestionKey && key === this.deletingSuggestionKey) {
        return;
      }
      this.useSuggestion(suggestion);
    },
    useSuggestion(suggestion) {
      if (!suggestion || suggestion.used) {
        return;
      }
      const trimmedQuestion = (suggestion.question || "").trim();
      if (!trimmedQuestion) {
        return;
      }

      const suggestionIndex = this.suggestedFaqs.findIndex(
        (item) => item.objectId === suggestion.objectId
      );
      const markSuggestionUsed = () => {
        if (suggestionIndex !== -1) {
          this.$set(this.suggestedFaqs, suggestionIndex, {
            ...this.suggestedFaqs[suggestionIndex],
            used: true,
          });
        }
      };

      let targetIndex = null;
      const existingIndex = this.faqs.findIndex((faq) => {
        if (suggestion.objectId && faq.objectId) {
          return faq.objectId === suggestion.objectId;
        }
        return (faq.question || "").trim() === trimmedQuestion;
      });
      if (existingIndex !== -1) {
        this.$set(this.faqs, existingIndex, {
          ...this.faqs[existingIndex],
          question: trimmedQuestion,
          answer: "",
          objectId: suggestion.objectId || this.faqs[existingIndex].objectId || null,
        });
        targetIndex = existingIndex;
      }

      if (targetIndex === null) {
        const emptyIndex = this.faqs.findIndex(
          (faq) => !(faq.question || "").trim() && !(faq.answer || "").trim()
        );
        if (emptyIndex !== -1) {
          this.$set(this.faqs, emptyIndex, {
            ...this.faqs[emptyIndex],
            question: trimmedQuestion,
            answer: "",
            objectId: suggestion.objectId || this.faqs[emptyIndex].objectId || null,
          });
          targetIndex = emptyIndex;
        }
      }

      if (targetIndex === null) {
        if (!this.canAddMore) {
          this.$bvToast.toast("คุณเพิ่มคำถามครบจำนวนสูงสุดแล้ว", {
            title: "เพิ่มคำถามไม่สำเร็จ",
            variant: "warning",
            solid: true,
          });
          return;
        }

        this.faqs.push(
          this.createFaq(
            {
              question: trimmedQuestion,
              answer: "",
              objectId: suggestion.objectId || null,
            },
            suggestion.objectId
          )
        );
        targetIndex = this.faqs.length - 1;
      }

      markSuggestionUsed();
      this.validationErrors = {};
      this.scrollToFaq(targetIndex);
    },
    async deleteSuggestion(suggestion) {
      if (!suggestion || this.isSaving || this.deletingSuggestionKey) {
        return;
      }
      const suggestionKey = this.getSuggestionKey(suggestion);
      if (!suggestionKey) {
        return;
      }

      const question = (suggestion.question || "").trim();
      const confirmMessage = question
        ? `คุณต้องการลบคำถามแนะนำ \"${question}\" หรือไม่?`
        : "คุณต้องการลบคำถามแนะนำนี้หรือไม่?";
      const confirmed = await this.$bvModal.msgBoxConfirm(
        confirmMessage,
        {
          title: "ยืนยันการลบ",
          okVariant: "danger",
          okTitle: "ลบ",
          cancelTitle: "ยกเลิก",
          footerClass: "justify-content-end",
          centered: true,
        }
      );
      if (!confirmed) {
        return;
      }

      this.deletingSuggestionKey = suggestionKey;
      try {
        if (suggestion.objectId) {
          let faqObject = this.faqObjects[suggestion.objectId];
          if (!faqObject) {
            const DraftFAQ = this.Parse.Object.extend("DraftFAQ");
            faqObject = new DraftFAQ();
            faqObject.id = suggestion.objectId;
          }
          await faqObject.destroy({ sessionToken: this.sessionToken });
          delete this.faqObjects[suggestion.objectId];
        }

        this.suggestedFaqs = this.suggestedFaqs.filter(
          (item) => this.getSuggestionKey(item) !== suggestionKey
        );

        this.$bvToast.toast("ลบคำถามแนะนำเรียบร้อยแล้ว", {
          title: "ลบสำเร็จ",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast("ไม่สามารถลบคำถามแนะนำได้ กรุณาลองใหม่อีกครั้ง", {
          title: "เกิดข้อผิดพลาด",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.deletingSuggestionKey = null;
      }
    },
    scrollToFaq(index) {
      if (index === null || index === undefined) {
        return;
      }
      this.$nextTick(() => {
        const rows = this.$refs.faqRows;
        const target = Array.isArray(rows) ? rows[index] : rows;
        if (target && typeof target.scrollIntoView === "function") {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    hasQuestionError(index) {
      return this.validationErrors[index] && this.validationErrors[index].question;
    },
    hasAnswerError(index) {
      return this.validationErrors[index] && this.validationErrors[index].answer;
    },
    sanitizeFaqs() {
      const errors = {};
      const sanitized = this.faqs.map((faq, index) => {
        const question = (faq.question || "").trim();
        const answer = (faq.answer || "").trim();
        const entryErrors = {};
        if (!question) {
          entryErrors.question = true;
        }
        if (!answer) {
          entryErrors.answer = true;
        }
        if (Object.keys(entryErrors).length) {
          errors[index] = entryErrors;
        }
        return { question, answer, objectId: faq.objectId || null };
      });
      if (Object.keys(errors).length) {
        this.validationErrors = errors;
        this.$bvToast.toast("กรุณากรอกคำถามและคำตอบให้ครบถ้วน", {
          title: "กรอกข้อมูลไม่ครบ",
          variant: "danger",
          solid: true,
        });
        return null;
      }
      this.validationErrors = {};
      return sanitized;
    },
    async saveFaqs() {
      const sanitizedFaqs = this.sanitizeFaqs();
      if (!sanitizedFaqs || !this.draftApartmentInfo) {
        return;
      }
      this.isSaving = true;
      try {
        const DraftFAQ = this.Parse.Object.extend("DraftFAQ");
        const saveObjects = [];
        const destroyObjects = [];
        const seenIds = new Set();

        sanitizedFaqs.forEach((faq) => {
          if (faq.objectId && this.faqObjects[faq.objectId]) {
            const faqObject = this.faqObjects[faq.objectId];
            faqObject.set("question", faq.question);
            faqObject.set("answer", faq.answer);
            faqObject.set("isActive", true);
            saveObjects.push(faqObject);
            seenIds.add(faq.objectId);
          } else {
            const newFaq = new DraftFAQ();
            newFaq.set("apartmentInfo", this.draftApartmentInfo);
            newFaq.set("question", faq.question);
            newFaq.set("answer", faq.answer);
            newFaq.set("isActive", true);
            let acl = new this.Parse.ACL()
            acl.setPublicReadAccess(true)
            acl.setPublicWriteAccess(false)
            if (this.draftApartmentInfo.get('apartment')) {
              acl.setRoleWriteAccess("Role_" + this.draftApartmentInfo.get('apartment').id, true)
            }
            newFaq.setACL(acl)
            saveObjects.push(newFaq);
          }
        });

        Object.keys(this.faqObjects).forEach((id) => {
          const faqObject = this.faqObjects[id];
          if (faqObject.get("isActive") && !seenIds.has(id)) {
            destroyObjects.push(faqObject);
          }
        });

        if (saveObjects.length) {
          await this.Parse.Object.saveAll(saveObjects, {
            sessionToken: this.sessionToken,
          });
        }
        if (destroyObjects.length) {
          await this.Parse.Object.destroyAll(destroyObjects, {
            sessionToken: this.sessionToken,
          });
        }
        await this.loadFaqEntries();

        this.$bvToast.toast("บันทึกคำถามเรียบร้อยแล้ว", {
          title: "บันทึกสำเร็จ",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast("ไม่สามารถบันทึกคำถามได้ กรุณาลองใหม่อีกครั้ง", {
          title: "เกิดข้อผิดพลาด",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.faq-container {
  margin-top: 40px;
  margin-bottom: 120px;
}

.faq-table td {
  vertical-align: top;
}

.faq-table textarea {
  min-height: 120px;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:disabled {
  cursor: not-allowed;
}

@media (max-width: 576px) {
  .faq-table textarea {
    min-height: 100px;
  }
}
</style>
